import { Injectable, OnDestroy } from "@angular/core";
import { GridApi, GridOptions} from "@ag-grid-community/core";
import { MatSnackBar } from "@angular/material/snack-bar";
import { PriceyardService } from 'src/app/modules/priceyard/services/priceyard.service';
import { ShipyardService } from 'src/app/modules/shipyard/shipyard.service';
import { HeaderService } from "src/app/components/header/header.service";
import { ReplaySubject, Subject, takeUntil } from "rxjs";
import { LoginModel } from "../models/login.model";
import { AuthService } from "../services/auth.service";
import { AmountInShipmentCellRendererComponent } from "../ag-grid-components/amount-in-shipment-cell-renderer/amount-in-shipment-cell-renderer.component";
import { BooleanCellRendererComponent } from "../ag-grid-components/boolean-cell-renderer/boolean-cell-renderer.component";
import { BooleanFilterComponent } from "../ag-grid-components/boolean-filter/boolean-filter.component";
import { BuyBoxCellEditorComponent } from "../ag-grid-components/buy-box--cell-editor/buy-box-cell-editor.component";
import { BuyBoxCellRendererComponent } from "../ag-grid-components/buy-box-cell-renderer/buy-box-cell-renderer.component";
import { CheckboxCellRendererComponent } from "../ag-grid-components/checkbox-cell-renderer/checkbox-cell-renderer.component";
import { DateCellRendererComponent } from "../ag-grid-components/date-cell-renderer/date-cell-renderer.component";
import { GridCheckboxHeaderRendererComponent } from "../ag-grid-components/grid-checkbox-header-renderer/grid-checkbox-header-renderer.component";
import { LoadingCellRendererComponent } from "../ag-grid-components/loading-cell-renderer/loading-cell-renderer.component";
import { MultiPriceCellRendererComponent } from "../ag-grid-components/multi-price-cell-renderer/multi-price-cell-renderer.component";
import { NoteCellRendererComponent } from "../ag-grid-components/note-cell-renderer/note-cell-renderer.component";
import { NumberAndTrendCellRendererComponent } from "../ag-grid-components/number-and-trend-cell-renderer/number-and-trend-cell-renderer.component";
import { OffersCellRendererComponent } from "../ag-grid-components/offers-cell-renderer/offers-cell-renderer.component";
import { PriceOverrideCellEditorComponent } from "../ag-grid-components/price-override-cell-editor/price-override-cell-editor.component";
import { PriceOverrideCellRendererComponent } from "../ag-grid-components/price-override-cell-renderer/price-override-cell-renderer.component";
import { RankCellRendererComponent } from "../ag-grid-components/rank-cell-renderer/rank-cell-renderer.component";
import { RepricingModelCellEditorComponent } from "../ag-grid-components/repricing-model-cell-editor/repricing-model-cell-editor.component";
import { RepricingModelCellRendererComponent } from "../ag-grid-components/repricing-model-cell-renderer/repricing-model-cell-renderer.component";
import { SimpleListCellRendererComponent } from "../ag-grid-components/simple-list-cell-renderer/simple-list-cell-renderer.component";
import { SinglePriceCellRendererComponent } from "../ag-grid-components/single-price-cell-renderer/single-price-cell-renderer.component";
import { SkuCellRendererComponent } from "../ag-grid-components/sku-cell-renderer/sku-cell-renderer.component";
import { SkuToolPanelComponent } from "../ag-grid-components/sku-tool-panel/sku-tool-panel.component";
import { StatusBarRendererComponent } from "../ag-grid-components/status-bar-renderer/status-bar-renderer.component";
import { StockHistoryCellRendererComponent } from "../ag-grid-components/stock-history-cell-renderer/stock-history-cell-renderer.component";
import { NumericCellEditorComponent } from "../components/numeric-cell-editor/numeric-cell-editor.component";
import { CustomHeaderTooltipComponent } from "../ag-grid-components/custom-header-tooltip/custom-header-tooltip.component";
import { GridActionsService } from "./grid-actions.service";
import { GridApiService } from "./grid-api.service";
import { SnackbarComponent } from "../components/snackbar/snackbar.component";
import { CustomGridHeaderComponent } from "../ag-grid-components/custom-grid-header/custom-grid-header.component";
import { ActionDetailButtonComponent } from "../components/action-detail-button/action-detail-button.component";
import { MultiSkuToolPanelComponent } from "../ag-grid-components/multi-sku-tool-panel/multi-sku-tool-panel.component";
import { SizeCellRendererComponent } from "../ag-grid-components/size-cell-renderer/size-cell-renderer.component";
import { ItemListCellRendererComponent } from "../ag-grid-components/item-list-cell-renderer/item-list-cell-renderer.component";
import { GridReadyEvent } from "@ag-grid-community/core";
import { AvgPriceTooltipCellRendererComponent } from "../ag-grid-components/avg-price-tooltip-cell-renderer/avg-price-tooltip-cell-renderer.component";
import { TagCellRendererComponent } from "../ag-grid-components/tag-cell-renderer/tag-cell-renderer.component";

@Injectable({
  providedIn: "root",
})
export class SkuGridBaseConfigService implements OnDestroy {
  gridApi: GridApi;
  accountFilterList$: ReplaySubject<[string]> = new ReplaySubject();
  accounts;
  userInfo: LoginModel;
  isGridReady: boolean = false;
  private destroy$ = new Subject<void>();

  constructor(
    private gridActionsService: GridActionsService,
    public headerService: HeaderService,
    private gridApiService: GridApiService,
    private authService: AuthService,
    private shipyardService: ShipyardService,
    // Dont remove line below, it is used
    private priceyardService: PriceyardService,
    private snackBar: MatSnackBar
  ) {
    this.gridApiService.gridApi$.pipe(takeUntil(this.destroy$)).subscribe((e: GridReadyEvent) => {
      this.gridApi = e.api;
      this.gridApi.closeToolPanel();
      this.gridApi.deselectAll();
      if (this.gridActionsService.isOpened) {
        this.gridActionsService.openSidePanel('singleSku');
      }
    });
    this.authService.userInfo$.pipe(takeUntil(this.destroy$)).subscribe((res) => (this.userInfo = res));
  }

  filterParams = {
    defaultOption: "inRange",
    maxNumConditions : 1,
    filterOptions: [
      "equals",
      "notEqual",
      "lessThan",
      "lessThanOrEqual",
      "greaterThan",
      "greaterThanOrEqual",
      {
        displayKey: "inRange",
        displayName: "Between",
        test: {},
      },
    ],
  };

  columnDefs = [
    {
      field: "",
      backendFields: ["AccountSkuId"],
      checkboxSelection: true,
      width: 50,
      //minWidth: 50,
      resizable: false,
      pinned: "left",
      lockPosition: true,
      suppressColumnsToolPanel: true,
      headerComponent: GridCheckboxHeaderRendererComponent,
    },
    {
      field: "Active",
      headerTooltip: 'Active',
      backendFields: ["Active"],
      filter: "booleanFilterComponent",
      hide: true,
      valueFormatter: (param) => (param.value ? "Yes" : "No"),
      width: 95,
      //minWidth:95
    },
    {
      field: "Sku",
      backendFields: [
        "Image",
        "Sku",
        "Asin",
        "Title",
        "AccountType",
        "FulfillmentType",
      ],
      headerName: "Listing",      
      headerTooltip: 'Listing',
      sortable: true,
      minWidth: 440,
      width:440,
      cellRenderer: "skuCellRendererComponent",
      filter: "agMultiColumnFilter",
      filterParams: {
        filters: [
          {
            filter: "agTextColumnFilter",
            display: "accordion",
            title: "SKU",           
            filterParams: {
              defaultOption: "contains",
              maxNumConditions : 1,
            },
          },
          {
            filter: "agTextColumnFilter",
            display: "accordion",
            title: "ASIN",
            filterParams: {
              defaultOption: "contains",
              maxNumConditions : 1,
            },
          },
          {
            display: "accordion",
            title: "Description",
            filter: "agTextColumnFilter",
            filterParams: {
              defaultOption: "contains",
              maxNumConditions : 1,
            },
          },
        ],
      },
    },
    {
      field: "TotalFbastock",
      headerName: "Total Channel Stock",
      headerTooltip: 'Total Channel Stock',
      width: 180,
      minWidth: 180,
      backendFields: ["FbaStock","FbaMinimumInventoryLevel"],
      sortable: true,
      cellRenderer: "stockHistoryCellRendererComponent",
        cellRendererParams: {
          props: {
            remainingProp: 'FbaStock',
            fbaMinimumInventoryLevel: 'FbaMinimumInventoryLevel',
          },
        },
      filter: "agNumberColumnFilter",
    },

    {
      field: "ProductsMapped",
      headerName: "Mapped Products",
      headerTooltip: "Mapped Products",
      backendFields: ["ProductsMapped"],
      hide:true,
      width: 120,
      minWidth: 120,
      filter: "agTextColumnFilter",
      filterParams: {
        defaultOption: "contains",
        maxNumConditions : 1,
      },
      sortable:true
    }, 
    {
      field: "FnSku",
      headerName: "FNSKU",
      headerTooltip: "FNSKU",
      backendFields: ["FnSku"],
      required:true,
      hide:true,
      sortable:false,
      width: 120,
      minWidth: 120,
      filter: "agTextColumnFilter",
    }, 
    {
      field: "FbaBBPrice",
      backendFields: [
        "BBAIsYours",
        "FbaBBPrice",
        "BBIsAmazon",
        "BBMIsYour",
        "FbmBBPrice",
        "Minpr",
        "BbPercent",
        "BbPercentageAvg"
      ],
      headerName: "BuyBox",
      headerTooltip: 'BuyBox',
      width: 160,
      minWidth: 80,
      sortable: true,
      cellRenderer: "buyBoxCellRendererComponent",
      cellEditor: "buyBoxCellEditorComponent",
      cellEditorParams: {
        priceProp: 'Minpr'
      },
      editable: () => true,
      filter: "agMultiColumnFilter",
      filterParams: {
        defaultOption: "inRange",
        maxNumConditions : 1,
        filters: [
          {
            filter: "agSetColumnFilter",
            display: "accordion",
            title: "BB Offer Type",
            filterParams: {
              values: ["FBA", "FBM", "Amazon"],
            },
          },
          {
            filter: "booleanFilterComponent",
            display: "accordion",
            title: "FBA BB Is Yours",
          },
          {
            filter: "booleanFilterComponent",
            display: "accordion",
            title: "FBM BB Is Yours",
          },
          {
            display: "accordion",
            title: "FBA BB Price",
            filter: "agNumberColumnFilter",
            filterParams: this.filterParams,
          },
          {
            filter: "agNumberColumnFilter",
            display: "accordion",
            title: "FBM BB Price",
            filterParams: this.filterParams,
          },
        ],
      },
    },
    {
      field: "AvailableFbastock",
      headerName: "Available Stock",
      headerTooltip: 'Available Stock',
      width: 100,
      minWidth: 100,
      sortable: true,
      filter: "agNumberColumnFilter",
      type: "numberColumn",
      backendFields: ["AvailableFbastock"],
      cellStyle: {
        height: "100%",
        display: "flex ",
        "align-items": "center ",
      },
      valueGetter: (params) => {
        return (Number(params.data.AvailableFbastock) || 0).toLocaleString('en');
      },
      cellClass: params => {
        return !params.value ? 'gray' : null;
      },
    },

    {
      field: "Price",
      headerName: "My Price",
      headerTooltip: 'My Price',
      backendFields: ["Price"],
      minWidth:100,
      width: 100,
      sortable: true,
      valueFormatter: (param) =>
        param.value ? "$" + (parseFloat(Number(param.value).toFixed(2))).toLocaleString('en') : "",
      filter: "agNumberColumnFilter",
      type: "numberColumn",
      cellStyle: {
        height: "100%",
        display: "flex ",
        "align-items": "center ",
      },
    },
    {
      field: "Account",
      headerTooltip: 'Account',
      backendFields: ["Account"],
      width: 140,
      minWidth:140,
      sortable: true,
      filter: false,
      hide: true,
    },
    {
      field: "Note",
      headerTooltip: 'Note',
      backendFields: ["Note"],
      width: 200,
      minWidth: 100,
      sortable: true,
      editable: true,
      cellRenderer: "noteCellRendererComponent",
      onCellValueChanged: (event) => this.gridActionsService.editNote(event),
      filter: "agTextColumnFilter",
      cellClass: "cell-vertical-align-text-center",
    },
    {
      headerName: "Fees",
      headerTooltip: "Fees",
      field: "Fees",
      backendFields: ["Fees"],
      filter: "agMultiColumnFilter",
      sortable:false,
      hide: true,
      filterParams: {
        filters: [
          {             
            display: "accordion",
            title: "ReferralFee",
            filter: "agNumberColumnFilter",
          },
          {
            display: "accordion",
            title: "FBAFee",
            filter: "agNumberColumnFilter",
          },
        ],
      },
      width: 160,
      minWidth:80,
      cellRenderer: "itemListCellRendererComponent",
      cellRendererParams: (params) => {
        return {
          props: [
            {
              label: "ReferralFee",
              price: params.data.ReferralFee,
              type: "currency",
            },
            {
              label: "FBAFee",
              price: params.data.FBAFee,
              type: "currency",
            },
            {
              label: "StorageFees",
              price: params.data.StorageFees,
              type: "currency",
            },
          ],
        };
      },
    },
    {
      field: "CaseQty",
      headerName: "Case QTY",
      headerTooltip: 'Case QTY',
      backendFields: ["CaseQty"],
      width: 100,
      minWidth: 50,
      sortable: true,
      cellEditor: "numericCellEditor",
      editable: true,
      filter: "agNumberColumnFilter",
      valueFormatter: (param) => (param.value ? Number(param.value).toLocaleString('en') : 0),
      cellClass: params => {
        return !params.value ? 'gray' : null;
      },
      onCellValueChanged: (event) => this.gridActionsService.editCaseQty(event,"CaseQty"),
    },
    {
      field: "UPC",
      headerTooltip: "UPC",
      backendFields: ["UPC"],
      filter: "agTextColumnFilter",
      width: 140,
      minWidth: 140,
      sortable: true,
      editable: true,
      onCellValueChanged: (params) => this.changeUPC(params),
    },
    {
      field: "Expirable",
      headerName: "Require Expiration",
      headerTooltip: "Require Expiration",
      backendFields: ["Expirable"],
      sortable: true,
      filter: "booleanFilterComponent",
      cellRenderer: "checkboxCellRendererComponent",
      maxWidth: 150,
      hide: true,
      cellRendererParams: {
        saveChanges: this.gridActionsService.saveRequireExpiration.bind(this,"Expirable"),
      },
      width: 95,
      minWidth: 95,
    },
    {
      field: "Prep",
      headerName: "Prep",
      headerTooltip: "Prep",
      backendFields: ["Prep"],
      width: 120,
      minWidth:120,
      hide: true,
      required:true,
    },
  ];

  components = {
    booleanCellRendererComponent: BooleanCellRendererComponent,
    buyBoxCellRendererComponent: BuyBoxCellRendererComponent,
    buyBoxCellEditorComponent: BuyBoxCellEditorComponent,
    noteCellRendererComponent: NoteCellRendererComponent,
    rankCellRendererComponent: RankCellRendererComponent,
    skuCellRendererComponent: SkuCellRendererComponent,
    statusBarRendererComponent: StatusBarRendererComponent,
    dateCellRendererComponent: DateCellRendererComponent,
    priceOverrideCellRendererComponent: PriceOverrideCellRendererComponent,
    priceOverrideCellEditorComponent: PriceOverrideCellEditorComponent,
    repricingModelCellRendererComponent: RepricingModelCellRendererComponent,
    repricingModelCellEditorComponent: RepricingModelCellEditorComponent,
    loadingCellRenderer: LoadingCellRendererComponent,
    offersCellRendererComponent: OffersCellRendererComponent,
    amountInShipmentCellRendererComponent:
      AmountInShipmentCellRendererComponent,
    skuToolPanelComponent: SkuToolPanelComponent,
    multiPriceCellRendererComponent: MultiPriceCellRendererComponent,
    singlePriceCellRendererComponent: SinglePriceCellRendererComponent,
    numberAndTrendCellRendererComponent: NumberAndTrendCellRendererComponent,
    simpleListCellRendererComponent: SimpleListCellRendererComponent,
    booleanFilterComponent: BooleanFilterComponent,
    gridCheckboxHeaderRendererComponent: GridCheckboxHeaderRendererComponent,
    customGridHeaderComponent:CustomGridHeaderComponent,
    numericCellEditor: NumericCellEditorComponent,
    stockHistoryCellRendererComponent: StockHistoryCellRendererComponent,
    checkboxCellRendererComponent: CheckboxCellRendererComponent,
    customHeaderTooltipComponent:CustomHeaderTooltipComponent,
    actionDetailButtonComponent:ActionDetailButtonComponent,
    multiSkuToolPanelComponent: MultiSkuToolPanelComponent,
    sizeCellRendererComponent: SizeCellRendererComponent,
    itemListCellRendererComponent:ItemListCellRendererComponent,
    avgPriceTooltipCellRendererComponent: AvgPriceTooltipCellRendererComponent,
    tagCellrenderer: TagCellRendererComponent,
  };

  gridOptions: GridOptions = {
    rowModelType: "serverSide",
    tooltipShowDelay:800,
    suppressServerSideInfiniteScroll: false,
    blockLoadDebounceMillis: 1,
    suppressCopyRowsToClipboard: true,
    defaultColDef: {
      resizable: true,
      filterParams: {
        maxNumConditions : 1,
      },
      menuTabs: ["filterMenuTab"],
      tooltipComponent:"customHeaderTooltipComponent",
    },
    components: this.components,

    columnTypes: {
      numberColumn: {
        filterParams: {
          defaultOption: "inRange",
          maxNumConditions : 1,
          filterOptions: [
            "equals",
            "notEqual",
            "lessThan",
            "lessThanOrEqual",
            "greaterThan",
            "greaterThanOrEqual",
            {
              displayKey: "inRange",
              displayName: "Between",
              test: {},
            },
          ],
        },
      },
    },

    statusBar: {
      statusPanels: [
        {
          statusPanel: "statusBarRendererComponent",
          align: "left",
          statusPanelParams: { model: false },
        },
      ],
    },
    rowHeight: 58,
    headerHeight: 50,
    rowSelection: "multiple",
    loadingCellRenderer: "loadingCellRenderer",
    sideBar: {
      toolPanels: [
        {
          id: "columns",
          labelDefault: "Columns",
          labelKey: "columns",
          iconKey: "columns",
          toolPanel: "agColumnsToolPanel",
        },
        {
          id: "filters",
          labelDefault: "Filters",
          labelKey: "filters",
          iconKey: "filter",
          toolPanel: "agFiltersToolPanel",
        },
        {
          id: "singleSku",
          labelDefault: "single sku",
          labelKey: "sku",
          iconKey: "sku",
          toolPanel: "skuToolPanelComponent",
        },
        {
          id: "multiSku",
          labelDefault: "multi sku",
          labelKey: "sku",
          iconKey: "sku",
          toolPanel: "multiSkuToolPanelComponent",
        },
      ],
    },
    cacheBlockSize: 100,
    pivotPanelShow: "onlyWhenPivoting",
    rowBuffer: 50,
    enableCellChangeFlash: true,
    suppressMenuHide: true,
    icons: {
      menu: '<span class="ag-icon ag-icon-filter" unselectable="on" role="presentation"></span>',
      filter:
        '<span class="ag-icon ag-icon-filter" style="display:none;" unselectable="on" role="presentation"></span>',
    },
    onGridReady: (e) => {
      this.gridActionsService.setFilter(e);
      this.gridApiService.gridApi$.next(e);
      this.gridActionsService.applycolumnState(e);
    },
    getRowId: (item:any) => item.data.AccountSkuId.toString(),
    onColumnVisible: (e) => this.gridActionsService.columnVisible(e,this.isGridReady),
    onColumnPinned: () => this.gridActionsService.saveColumnState(null,this.isGridReady),
    onDragStopped: () => {
      this.gridActionsService.saveColumnState(null,this.isGridReady)
    },
    onSortChanged: () => this.gridActionsService.saveColumnState(null,this.isGridReady),
    onFilterChanged: () => this.gridActionsService.updateSelectedFilters(null),
    onCellKeyDown: (e) => this.gridActionsService.onCellKeyDown(e),
    onRowClicked: (e) => {
      if(this.priceyardService.isPriceyardPanel && localStorage.getItem("toolPanel") === "false"){
        this.gridActionsService.onRowClicked(e);
        this.priceyardService.openSidenav$.next(e);
      }
    },
    onFirstDataRendered:() => this.isGridReady = true,
    onRowSelected: ($event) => {
      this.addToShipment($event);
        this.gridActionsService.updateSelectedRows($event);
        if(localStorage.getItem("toolPanel") !== "false"){
        const selectedRows = this.gridApi.getSelectedRows();
        if (selectedRows.length > 1) {
          this.gridActionsService.openSidePanel("multiSku");
          this.refreshMultiSkuPanel();
        } else {
          this.gridActionsService.openSidePanel("singleSku");
          this.refreshSingleSkuPanel();
        }
        }
    },
  };

  refreshSingleSkuPanel() {
    const singleToolpanelComponent = this.gridApi.getToolPanelInstance('singleSku');
    if (singleToolpanelComponent) {
      singleToolpanelComponent.refresh(null);
    }
  }

  refreshMultiSkuPanel() {
    const multiToolpanelComponent = this.gridApi.getToolPanelInstance('multiSku');
    if (multiToolpanelComponent) {
      multiToolpanelComponent.refresh(null);
    }
  }

  setBuyBoxCellRendererParams(columnDefs, yard: string) {
    const index = columnDefs.findIndex(
      (res) => res.cellRenderer === "buyBoxCellRendererComponent"
    );
    if (index !== -1) {
      columnDefs[index].cellRendererParams = {
        props: yard,
        isPriceYard: this.userInfo.isPriceYard !== null,
      };
    }
    return columnDefs;
  }

  addToShipment($event) {
    const index = this.shipyardService.skusToAddToShipment
      .map((e) => e.sku.AccountSkuId)
      .indexOf($event.node.data.AccountSkuId);

    if ($event.node.isSelected()) {
      if (index === -1) {
        this.shipyardService.skusToAddToShipment.push({sku: $event.node.data, qty: 0});
      }
    } else {
      if (index > -1 && this.shipyardService.skusToAddToShipment[index].qty === 0) {
        this.shipyardService.skusToAddToShipment.splice(index, 1);
      }
    };
  }

  changeUPC(params) {
    const sku = [
      {
        sku: params.data.Sku,
        account: params.data.Account,
        accountSkuId: params.data.AccountSkuId,
        otherChanges: [
          {
            name: "Upc",
            stringValue: params.newValue,
          },
        ],
      },
    ];
    this.priceyardService.changePriceFactor(sku).pipe(takeUntil(this.destroy$)).subscribe((e) => {      
      this.snackBar.openFromComponent(SnackbarComponent,
        {
          duration: 3000,
          horizontalPosition: 'center',
          data: {
            title: 'UPC successfully changed',
            action: 'Dismiss',
            type: 'success',
          },
        }
    );
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.unsubscribe();
  }
}
